import type { AccessCatalogItemPublic, AccessPasswordHandling } from '@accounts/shared';

import { STR } from '../../../i18n';
import type {
  CreateAccessRecordBody,
  CreateOnBehalfAccessRequestBody,
  SettableAccessRecordStatus,
} from '../../../lib/api';

import { itemLabel } from './accessFormat';

/**
 * The add form's "What" picker, kept pure. Live rows the person does not hold yet: outside
 * systems they have no live record on and, where suite apps are offered (the Matrix, FINAL
 * demo `addAccessModal`), suite-app rows whose application they hold no live grant on. Suite
 * apps first, then outside systems, each by name. `keepItemId` (the clicked cell's row) is
 * always kept so the preselect is never dropped. Requestable is ignored: rows hidden from
 * requests can still be added by an administrator.
 */
export function whatOptions({
  catalog,
  heldItemIds,
  grantedAppIds,
  keepItemId,
  withSuiteApps,
}: {
  catalog: readonly AccessCatalogItemPublic[];
  heldItemIds: ReadonlySet<string>;
  grantedAppIds: ReadonlySet<string>;
  keepItemId: string | undefined;
  withSuiteApps: boolean;
}): AccessCatalogItemPublic[] {
  const byLabel = (a: AccessCatalogItemPublic, b: AccessCatalogItemPublic) =>
    itemLabel(a.path, a.name).localeCompare(itemLabel(b.path, b.name));
  const live = catalog.filter((item) => item.retiredAt === null);
  const outside = live
    .filter((item) => item.application === null)
    .filter((item) => item.id === keepItemId || !heldItemIds.has(item.id))
    .sort(byLabel);
  if (!withSuiteApps) return outside;
  const apps = live
    .filter((item) => item.application !== null)
    .filter(
      (item) =>
        item.id === keepItemId ||
        (item.application !== null && !grantedAppIds.has(item.application.id)),
    )
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  return [...apps, ...outside];
}

export function whatOptionLabel(item: AccessCatalogItemPublic): string {
  return item.application === null
    ? itemLabel(item.path, item.name)
    : STR.access.record.suiteAppOption(item.name);
}

/** The picker's value: the picked row while it is on offer, otherwise nothing picked. */
export function whatValue(options: readonly AccessCatalogItemPublic[], picked: string): string {
  return options.some((item) => item.id === picked) ? picked : '';
}

/**
 * HR (or IT) without administrator rights, on an outside-system row not flagged "HR can
 * grant directly": the add form files a request for an administrator instead of a record.
 */
export function hrMustRequest(hrOnly: boolean, item: AccessCatalogItemPublic | null): boolean {
  return hrOnly && item !== null && item.application === null && !item.hrCanGrant;
}

export type AddAccessSubmission =
  | { kind: 'invalid'; message: string }
  | { kind: 'grant'; body: { userId: string; applicationId: string } }
  | { kind: 'request'; body: CreateOnBehalfAccessRequestBody }
  | { kind: 'record'; body: CreateAccessRecordBody };

/** What the add form's submit does with the fields as they stand, kept pure. */
export function addAccessSubmission(form: {
  personId: string;
  item: AccessCatalogItemPublic | null;
  hrOnly: boolean;
  login: string;
  caseLogin: string;
  status: Extract<SettableAccessRecordStatus, 'active' | 'needs_access'>;
  mode: AccessPasswordHandling | '';
  notes: string;
}): AddAccessSubmission {
  const { personId, item } = form;
  if (personId === '') return { kind: 'invalid', message: STR.access.record.personRequired };
  if (item === null) return { kind: 'invalid', message: STR.access.record.itemRequired };
  if (item.application !== null) {
    return { kind: 'grant', body: { userId: personId, applicationId: item.application.id } };
  }
  if (hrMustRequest(form.hrOnly, item)) {
    return { kind: 'request', body: { userId: personId, catalogItemId: item.id } };
  }
  if (form.mode === '') return { kind: 'invalid', message: STR.access.record.passwordRequired };
  const login = form.login.trim();
  const caseLogin = form.caseLogin.trim();
  const notes = form.notes.trim();
  return {
    kind: 'record',
    body: {
      userId: personId,
      catalogItemId: item.id,
      login: login === '' ? null : login,
      caseLogin: caseLogin === '' ? null : caseLogin,
      status: form.status,
      passwordHandling: form.mode,
      notes: notes === '' ? null : notes,
    },
  };
}
